import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { Menu, X, Warehouse, BarChart3, Package, MapPin, Smartphone, QrCode, Cpu, Radio, Factory, ShoppingCart, Heart, Briefcase, ChevronRight } from 'lucide-react';
import logo from '../assets/images/NewLogoCut.png';
const MegaMenu = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeMenu, setActiveMenu] = useState(null);
  const location = useLocation();

  const isActive = (path) => location.pathname === path;

  const servicesMenu = [
    {
      icon: <Warehouse className="w-6 h-6" />,
      title: 'WMS',
      subtitle: 'Warehouse Management System',
      description: 'Complete warehouse automation and optimization',
      link: '/services/wms',
      color: 'blue'
    },
    {
      icon: <BarChart3 className="w-6 h-6" />,
      title: 'WIP Tracking',
      subtitle: 'Work in Progress',
      description: 'Real-time production monitoring',
      link: '/services/wip',
      color: 'cyan'
    },
    {
      icon: <Package className="w-6 h-6" />,
      title: 'Inventory Tracking',
      subtitle: 'Real-time inventory management',
      description: 'Advanced stock control and forecasting',
      link: '/services/inventory',
      color: 'blue'
    },
    {
      icon: <MapPin className="w-6 h-6" />,
      title: 'Asset Tracking',
      subtitle: 'Track and manage assets',
      description: 'Complete asset lifecycle management',
      link: '/services/asset-tracking',
      color: 'cyan'
    }
  ];

  const solutionsMenu = [
    {
      icon: <Smartphone className="w-6 h-6" />,
      title: 'Mobile Applications',
      subtitle: 'Custom mobile solutions',
      description: 'iOS and Android apps for field operations',
      link: '/solutions/mobile-apps',
      color: 'blue'
    },
    {
      icon: <QrCode className="w-6 h-6" />,
      title: 'Barcode Scanning',
      subtitle: 'Efficient scanning devices',
      description: 'High-speed 1D and 2D barcode solutions',
      link: '/solutions/barcode',
      color: 'cyan'
    },
    {
      icon: <Cpu className="w-6 h-6" />,
      title: 'IoT Solutions',
      subtitle: 'Connected device integration',
      description: 'Smart sensors and automated monitoring',
      link: '/solutions/iot',
      color: 'blue'
    },
    {
      icon: <Radio className="w-6 h-6" />,
      title: 'RFID Technology',
      subtitle: 'Advanced RFID tracking',
      description: 'Contactless automated data capture',
      link: '/solutions/rfid',
      color: 'cyan'
    }
  ];

  const industriesMenu = [
    {
      icon: <Factory className="w-6 h-6" />,
      title: 'Manufacturing',
      description: 'Production and assembly operations',
      link: '/industries/manufacturing'
    },
    {
      icon: <ShoppingCart className="w-6 h-6" />,
      title: 'Retail & E-commerce',
      description: 'Inventory and order fulfillment',
      link: '/industries/retail'
    },
    {
      icon: <Heart className="w-6 h-6" />,
      title: 'Healthcare',
      description: 'Medical supplies and equipment tracking',
      link: '/industries/healthcare'
    },
    {
      icon: <Briefcase className="w-6 h-6" />,
      title: 'Logistics & Distribution',
      description: 'Supply chain and transportation',
      link: '/industries/logistics'
    }
  ];

  const handleMouseEnter = (menu) => {
    setActiveMenu(menu);
  };

  const handleMouseLeave = () => {
    setActiveMenu(null);
  };

  return (
    <nav className="bg-white shadow-lg sticky top-0 z-50">
      <div className="container mx-auto max-w-7xl px-4">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <Link to="www.svits.com" className="flex items-center space-x-2 group">
            <div className="text-2xl font-bold transition-transform duration-300 group-hover:scale-105">
              {/* <span className="text-slate-900">Sight</span>
              <span className="text-blue-600"> Vision</span> */}
                <img src={logo}  alt="Logo" className="mb-4 svlogo" />
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-1">
            <Link
              to="/"
              className={`px-4 py-2 rounded-lg text-base font-medium transition-all duration-200 ${
                isActive('/') 
                  ? 'text-blue-600 bg-blue-50' 
                  : 'text-slate-700 hover:text-blue-600 hover:bg-slate-50'
              }`}
            >
              Home
            </Link>

            {/* Services Mega Menu */}
            <div
              className="relative"
              onMouseEnter={() => handleMouseEnter('services')}
              onMouseLeave={handleMouseLeave}
            >
              <button className="px-4 py-2 rounded-lg text-base font-medium text-slate-700 hover:text-blue-600 hover:bg-slate-50 transition-all duration-200 flex items-center space-x-1">
                <span>Services</span>
                <ChevronRight className={`w-4 h-4 transition-transform duration-200 ${activeMenu === 'services' ? 'rotate-90' : ''}`} />
              </button>
              
              {activeMenu === 'services' && (
                <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-2 w-[800px] bg-white rounded-xl shadow-2xl border border-slate-200 py-6 px-8 animate-in fade-in slide-in-from-top-2 duration-200">
                  <div className="grid grid-cols-2 gap-4">
                    {servicesMenu.map((item, index) => (
                      <Link
                        key={index}
                        to={item.link}
                        className="group p-4 rounded-lg hover:bg-gradient-to-r hover:from-blue-50 hover:to-cyan-50 transition-all duration-300 border border-transparent hover:border-blue-200"
                        onClick={() => setActiveMenu(null)}
                      >
                        <div className="flex items-start space-x-4">
                          <div className={`p-3 rounded-lg bg-${item.color}-100 text-${item.color}-600 group-hover:scale-110 transition-transform duration-300`}>
                            {item.icon}
                          </div>
                          <div className="flex-1">
                            <h3 className="text-lg font-bold text-slate-900 mb-1 group-hover:text-blue-600 transition-colors">
                              {item.title}
                            </h3>
                            <p className="text-sm font-medium text-slate-600 mb-1">{item.subtitle}</p>
                            <p className="text-sm text-slate-500">{item.description}</p>
                          </div>
                          <ChevronRight className="w-5 h-5 text-slate-400 group-hover:text-blue-600 group-hover:translate-x-1 transition-all duration-300" />
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Solutions Mega Menu */}
            <div
              className="relative"
              onMouseEnter={() => handleMouseEnter('solutions')}
              onMouseLeave={handleMouseLeave}
            >
              <button className="px-4 py-2 rounded-lg text-base font-medium text-slate-700 hover:text-blue-600 hover:bg-slate-50 transition-all duration-200 flex items-center space-x-1">
                <span>Solutions</span>
                <ChevronRight className={`w-4 h-4 transition-transform duration-200 ${activeMenu === 'solutions' ? 'rotate-90' : ''}`} />
              </button>
              
              {activeMenu === 'solutions' && (
                <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-2 w-[800px] bg-white rounded-xl shadow-2xl border border-slate-200 py-6 px-8 animate-in fade-in slide-in-from-top-2 duration-200">
                  <div className="grid grid-cols-2 gap-4">
                    {solutionsMenu.map((item, index) => (
                      <Link
                        key={index}
                        to={item.link}
                        className="group p-4 rounded-lg hover:bg-gradient-to-r hover:from-blue-50 hover:to-cyan-50 transition-all duration-300 border border-transparent hover:border-blue-200"
                        onClick={() => setActiveMenu(null)}
                      >
                        <div className="flex items-start space-x-4">
                          <div className={`p-3 rounded-lg bg-${item.color}-100 text-${item.color}-600 group-hover:scale-110 transition-transform duration-300`}>
                            {item.icon}
                          </div>
                          <div className="flex-1">
                            <h3 className="text-lg font-bold text-slate-900 mb-1 group-hover:text-blue-600 transition-colors">
                              {item.title}
                            </h3>
                            <p className="text-sm font-medium text-slate-600 mb-1">{item.subtitle}</p>
                            <p className="text-sm text-slate-500">{item.description}</p>
                          </div>
                          <ChevronRight className="w-5 h-5 text-slate-400 group-hover:text-blue-600 group-hover:translate-x-1 transition-all duration-300" />
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Industries Mega Menu */}
            <div
              className="relative"
              onMouseEnter={() => handleMouseEnter('industries')}
              onMouseLeave={handleMouseLeave}
            >
              <button className="px-4 py-2 rounded-lg text-base font-medium text-slate-700 hover:text-blue-600 hover:bg-slate-50 transition-all duration-200 flex items-center space-x-1">
                <span>Industries</span>
                <ChevronRight className={`w-4 h-4 transition-transform duration-200 ${activeMenu === 'industries' ? 'rotate-90' : ''}`} />
              </button>
              
              {activeMenu === 'industries' && (
                <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-2 w-[600px] bg-white rounded-xl shadow-2xl border border-slate-200 py-6 px-8 animate-in fade-in slide-in-from-top-2 duration-200">
                  <div className="grid grid-cols-2 gap-4">
                    {industriesMenu.map((item, index) => (
                      <Link
                        key={index}
                        to={item.link}
                        className="group p-4 rounded-lg hover:bg-gradient-to-r hover:from-blue-50 hover:to-cyan-50 transition-all duration-300 border border-transparent hover:border-blue-200"
                        onClick={() => setActiveMenu(null)}
                      >
                        <div className="flex items-start space-x-3">
                          <div className="p-3 rounded-lg bg-blue-100 text-blue-600 group-hover:scale-110 transition-transform duration-300">
                            {item.icon}
                          </div>
                          <div className="flex-1">
                            <h3 className="text-base font-bold text-slate-900 mb-1 group-hover:text-blue-600 transition-colors">
                              {item.title}
                            </h3>
                            <p className="text-sm text-slate-500">{item.description}</p>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <Link
              to="/about"
              className={`px-4 py-2 rounded-lg text-base font-medium transition-all duration-200 ${
                isActive('/about') 
                  ? 'text-blue-600 bg-blue-50' 
                  : 'text-slate-700 hover:text-blue-600 hover:bg-slate-50'
              }`}
            >
              About
            </Link>

            <Link to="/contact">
              <Button className="ml-2 bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white transition-all duration-300 hover:scale-105 shadow-lg hover:shadow-xl">
                Contact Us
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="lg:hidden text-slate-700 hover:text-blue-600 transition-colors p-2"
          >
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isOpen && (
          <div className="lg:hidden py-4 border-t border-slate-200 animate-in slide-in-from-top duration-300">
            <div className="flex flex-col space-y-2">
              <Link
                to="/"
                className="px-4 py-2 text-slate-700 hover:text-blue-600 hover:bg-slate-50 rounded-lg font-medium transition-colors"
                onClick={() => setIsOpen(false)}
              >
                Home
              </Link>
              
              {/* Mobile Services */}
              <div className="px-4 py-2">
                <div className="font-semibold text-slate-900 mb-2">Services</div>
                <div className="ml-4 space-y-1">
                  {servicesMenu.map((item, index) => (
                    <Link
                      key={index}
                      to={item.link}
                      className="block py-2 text-sm text-slate-600 hover:text-blue-600"
                      onClick={() => setIsOpen(false)}
                    >
                      {item.title}
                    </Link>
                  ))}
                </div>
              </div>

              {/* Mobile Solutions */}
              <div className="px-4 py-2">
                <div className="font-semibold text-slate-900 mb-2">Solutions</div>
                <div className="ml-4 space-y-1">
                  {solutionsMenu.map((item, index) => (
                    <Link
                      key={index}
                      to={item.link}
                      className="block py-2 text-sm text-slate-600 hover:text-blue-600"
                      onClick={() => setIsOpen(false)}
                    >
                      {item.title}
                    </Link>
                  ))}
                </div>
              </div>

              {/* Mobile Industries */}
              <div className="px-4 py-2">
                <div className="font-semibold text-slate-900 mb-2">Industries</div>
                <div className="ml-4 space-y-1">
                  {industriesMenu.map((item, index) => (
                    <Link
                      key={index}
                      to={item.link}
                      className="block py-2 text-sm text-slate-600 hover:text-blue-600"
                      onClick={() => setIsOpen(false)}
                    >
                      {item.title}
                    </Link>
                  ))}
                </div>
              </div>

              <Link
                to="/about"
                className="px-4 py-2 text-slate-700 hover:text-blue-600 hover:bg-slate-50 rounded-lg font-medium transition-colors"
                onClick={() => setIsOpen(false)}
              >
                About
              </Link>
              
              <Link to="/contact" onClick={() => setIsOpen(false)}>
                <Button className="w-full mt-2 bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white">
                  Contact Us
                </Button>
              </Link>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default MegaMenu;